import React, { forwardRef, ReactNode, Ref, useMemo } from "react";
import { VARIANT } from "src/Modules/ThemeModule/Types/CommonTypes";
import useThemeValue from "src/Modules/ThemeModule/Hooks/useThemeValue";
import classNames from "classnames";

export type BlockProps = {
  children?: ReactNode;
  variant?: VARIANT;
  elevation?: number;
  style?: React.CSSProperties;
  backgroundColor?: string;
  pointerEvents?: "auto" | "none";
  testID?: string;
  marginVertical?: number | string;
  marginHorizontal?: number | string;
  paddingVertical?: number | string;
  paddingHorizontal?: number | string;
  component?: React.ElementType;
} & React.HTMLAttributes<HTMLSpanElement> &
  React.CSSProperties;

function Block(props: BlockProps, ref: Ref<HTMLSpanElement>) {
  const {
    children,
    elevation = 0,
    style,
    variant,
    backgroundColor,
    pointerEvents,
    testID,
    marginVertical,
    marginHorizontal,
    paddingVertical,
    paddingHorizontal,
    component: Component = "div",
    className = "",
    ...styleProps
  } = props;
  const theme = useThemeValue();

  const webShadowElevation = useMemo(
    () =>
      elevation === 0
        ? {}
        : {
            boxShadow: `0 ${0.6 * elevation}px ${
              0.54 * elevation
            }px rgba(0, 0, 0, ${0.0015 * elevation + 0.18})`,
          },
    [elevation]
  );

  // Generate Tailwind classes based on props
  const tailwindClasses = useMemo(() => {
    return classNames(
      // Base classes
      className,
      // Margin
      marginVertical !== undefined && `my-${marginVertical}`,
      marginHorizontal !== undefined && `mx-${marginHorizontal}`,
      // Padding
      paddingVertical !== undefined && `py-${paddingVertical}`,
      paddingHorizontal !== undefined && `px-${paddingHorizontal}`,
      // Pointer events
      pointerEvents === "none" && "pointer-events-none",
      pointerEvents === "auto" && "pointer-events-auto",
      // Ensure Block behaves as a flex item when inside a flex container
      "flex-shrink-0" // Prevent shrinking (optional, depending on your use case)
    );
  }, [
    className,
    marginVertical,
    marginHorizontal,
    paddingVertical,
    paddingHorizontal,
    pointerEvents,
    elevation,
  ]);

  const customStyles = useMemo(() => {
    const styles: React.CSSProperties = {};

    if (marginVertical !== undefined) {
      styles.marginTop = marginVertical;
      styles.marginBottom = marginVertical;
    }

    if (marginHorizontal !== undefined) {
      styles.marginLeft = marginHorizontal;
      styles.marginRight = marginHorizontal;
    }

    if (paddingVertical !== undefined) {
      styles.paddingTop = paddingVertical;
      styles.paddingBottom = paddingVertical;
    }

    if (paddingHorizontal !== undefined) {
      styles.paddingLeft = paddingHorizontal;
      styles.paddingRight = paddingHorizontal;
    }

    return styles;
  }, [marginVertical, marginHorizontal, paddingVertical, paddingHorizontal]);

  return (
    <Component
      ref={ref}
      className={tailwindClasses}
      style={{
        pointerEvents,
        ...webShadowElevation,
        ...styleProps,
        ...customStyles,
        backgroundColor: backgroundColor
          ? backgroundColor
          : variant
          ? theme.colors[variant]
          : undefined,
        ...style,
      }}
      {...styleProps}
      data-testid={testID}
    >
      {children}
    </Component>
  );
}

export default forwardRef(Block);
